// Admin — Analytics. Phase 2 surfaces audit_log rollups: per-day action
// counts, per-tool counts, top users. Phase 5 will add cost breakdown
// once extraction_meta carries token + USD figures.
import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api } from "../../lib/api";
import type { AnalyticsSummary } from "../../../../shared/types/admin";

export function Analytics() {
  const [data, setData] = useState<(AnalyticsSummary & { daysWindow: number }) | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const r = await api<AnalyticsSummary & { daysWindow: number }>(
          "/api/admin/analytics?days=30",
        );
        setData(r);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, []);

  return (
    <>
      <div className="bsp-page-header">
        <h1 className="bsp-page-title">Analytics</h1>
      </div>

      {error && (
        <div className="bsp-card" style={{ borderColor: "var(--bsp-red)" }}>
          <p style={{ color: "var(--bsp-red-fg)", margin: 0 }}>{error}</p>
        </div>
      )}

      {!data && !error && <p>Loading analytics…</p>}

      {data && (
        <div style={{ display: "grid", gap: 16 }}>
          <div className="bsp-card">
            <div className="bsp-card-title-row">
              <div className="bsp-eyebrow">Last {data.daysWindow} days</div>
            </div>
            <h3 style={{ marginTop: 0 }}>Actions per day</h3>
            <div style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.extractionsByDay}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--bsp-border)" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="var(--bsp-blue)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
            }}
          >
            <div className="bsp-card">
              <h3 style={{ marginTop: 0 }}>Actions by tool</h3>
              {data.extractionsByTool.length === 0 ? (
                <p style={{ color: "var(--bsp-fg-muted)" }}>No tool actions recorded.</p>
              ) : (
                <table className="bsp-table">
                  <thead>
                    <tr>
                      <th>Tool</th>
                      <th style={{ textAlign: "right" }}>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.extractionsByTool.map((r) => (
                      <tr key={r.toolId}>
                        <td className="bsp-mono">{r.toolId || "(none)"}</td>
                        <td className="bsp-mono" style={{ textAlign: "right" }}>
                          {r.count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="bsp-card">
              <h3 style={{ marginTop: 0 }}>Top users</h3>
              {data.topUsers.length === 0 ? (
                <p style={{ color: "var(--bsp-fg-muted)" }}>No user activity recorded.</p>
              ) : (
                <table className="bsp-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th style={{ textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topUsers.map((u) => (
                      <tr key={u.userId}>
                        <td>{u.displayName || `user #${u.userId}`}</td>
                        <td className="bsp-mono" style={{ textAlign: "right" }}>
                          {u.count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
